// src/pages/etc/mailAlert/MailAlertSettingsTab.jsx
// ✅ "설정" 탭 — 키워드 CRUD(ExpenseUsageCodes.jsx 톤) + 수신자 관리(직원 검색
// 자동완성, ApprovalLineModal.jsx/IncomeDraftNew.jsx 거래처 자동완성과 동일
// 상호작용 패턴) + 조회 상태·재알림 설정.
import React, { useEffect, useState } from "react";
import api from "../../../api";
import MailCheckRunsPanel from "./MailCheckRunsPanel";

const ROW_STYLE = { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #eee" };
const SECTION_STYLE = { background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: 16 };
const SECTION_TITLE_STYLE = { margin: "0 0 12px", fontSize: 16, fontWeight: 700, whiteSpace: "nowrap" };
const SMALL_BTN_STYLE = { width: 32, height: 32, flexShrink: 0, border: "none", borderRadius: 6, cursor: "pointer", background: "#fff1f2", color: "#be123c" };
const INPUT_STYLE = { height: 36, padding: "0 10px", border: "1px solid #ced4da", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };
const PRIMARY_BTN_STYLE = { height: 36, padding: "0 16px", flexShrink: 0, whiteSpace: "nowrap", border: "none", borderRadius: 6, background: "#4a90e2", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" };

// ✅ 3열 가로 배치 — 키워드 | 수신자 | 조회 상태·재알림 설정.
// auto-fit + minmax라 폭이 모자라면(태블릿 등) 열 수가 3→2→1로 알아서 줄어
// 세로로 떨어진다. 미디어쿼리 없이 컨테이너 폭만으로 동작한다.
const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: 16,
  alignItems: "start",   // 패널 높이가 서로 달라도 위로 정렬(늘어나지 않게)
};

// ✅ 키워드/수신자 이름 — flex:1로 늘려두면 좁은 패널에서도 이름과 활성 체크·삭제
// 버튼 사이가 벌어진다(기존 문제). 늘어나지 않고(0 1 auto) 줄어들기만 하게 해서
// 컨트롤이 이름 바로 옆에 붙게 하고, 너무 길면 줄바꿈 대신 말줄임으로 처리한다.
const NAME_STYLE = {
  flex: "0 1 auto", minWidth: 0,
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};

// 조회 상태 신선도 — 10분 주기라 20분을 넘으면 조회가 멈춘 것으로 본다.
const STALE_MINUTES = 20;

// 제외 조건 태그 — 키워드명 옆이 아니라 접이식 영역 안에만 나온다(목록은 깔끔하게 유지).
const TAG_STYLE = {
  display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
  background: "#eef2ff", color: "#3730a3", borderRadius: 12,
  padding: "2px 4px 2px 8px", fontSize: 12, maxWidth: "100%",
};
const TAG_X_STYLE = {
  width: 18, height: 18, flexShrink: 0, border: "none", borderRadius: "50%",
  background: "transparent", color: "#3730a3", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 0,
};
const LINK_BTN_STYLE = {
  width: "auto", height: "auto", flexShrink: 0, whiteSpace: "nowrap",
  border: "none", background: "transparent", color: "#4a90e2",
  fontSize: 12, cursor: "pointer", padding: 0,
};

export default function MailAlertSettingsTab({ onOpenInbox }) {
  // 이력 패널 강제 갱신용 — "지금 확인"이 끝나면 올려서 즉시 새 회차를 보이게 한다.
  const [runsRefreshKey, setRunsRefreshKey] = useState(0);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");

  const [recipients, setRecipients] = useState([]);
  const [staffInput, setStaffInput] = useState("");
  const [staffResults, setStaffResults] = useState([]);
  const [showStaffList, setShowStaffList] = useState(false);

  const [status, setStatus] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [checkingNow, setCheckingNow] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [retroState, setRetroState] = useState(null);   // 백그라운드 재검색 진행 상황
  const [openExclude, setOpenExclude] = useState(null); // 제외 조건을 펼친 키워드 id
  const [excludeDraft, setExcludeDraft] = useState({ only: "", sender: "", word: "" });
  // 화면에 떠 있는 동안 "마지막 조회 성공"이 몇 분 전인지 다시 그리기 위한 tick
  const [nowTick, setNowTick] = useState(Date.now());

  async function loadKeywords() {
    try {
      const { data } = await api.get("/mail-alert/keywords/");
      setKeywords(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 키워드 목록 불러오기 실패:", err);
    }
  }

  async function loadRecipients() {
    try {
      const { data } = await api.get("/mail-alert/recipients/");
      setRecipients(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 수신자 목록 불러오기 실패:", err);
    }
  }

  async function loadStatus() {
    try {
      const { data } = await api.get("/mail-alert/status/");
      setStatus(data);
    } catch (err) {
      console.error("❌ 상태 조회 실패:", err);
    }
  }

  useEffect(() => { loadKeywords(); loadRecipients(); loadStatus(); }, []);

  // ✅ 조회 상태 자동 갱신 — 예전엔 탭을 열 때 한 번만 조회해서, 정기 조회가 계속
  // 정상인데도 화면에는 몇 시간 전 시각이 그대로 떠 있었다("조회가 멈춘 것 같다"는
  // 오인의 원인). 30초마다 다시 읽고, 경과 시간 표시는 10초마다 다시 그린다.
  useEffect(() => {
    const reload = setInterval(loadStatus, 30_000);
    const tick = setInterval(() => setNowTick(Date.now()), 10_000);
    return () => { clearInterval(reload); clearInterval(tick); };
  }, []);

  // 그 키워드에 걸린 제외 조건 개수 (발신자 + 단어)
  const excludeCount = (kw) =>
    (kw.include_senders?.length || 0) + (kw.exclude_senders?.length || 0) + (kw.exclude_words?.length || 0);

  // 마지막 조회 성공이 몇 분 전인지 (없으면 null)
  const staleMinutes = status?.last_success_at
    ? Math.floor((nowTick - new Date(status.last_success_at).getTime()) / 60000)
    : null;

  useEffect(() => {
    const q = staffInput.trim();
    if (!q) { setStaffResults([]); return; }
    let ignore = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/staff-contacts/", { params: { q } });
        const list = Array.isArray(data) ? data : data.results || [];
        if (!ignore) setStaffResults(list);
      } catch (err) {
        console.error("❌ 직원 검색 실패:", err);
      }
    }, 200);
    return () => { ignore = true; clearTimeout(t); };
  }, [staffInput]);

  async function addKeyword() {
    const kw = newKeyword.trim();
    if (!kw) return;
    try {
      // ✅ 서버가 등록 직후 최근 30일을 이 키워드로 소급 스캔한다(응답의 retro_scan).
      // 커서(last_uid)가 이미 지나간 메일은 정기 조회로는 영영 안 잡히기 때문 —
      // 결과를 바로 알려줘야 "등록했는데 왜 안 뜨지"가 안 생긴다.
      const { data } = await api.post("/mail-alert/keywords/", { keyword: kw, is_active: true });
      setNewKeyword("");
      loadKeywords();
      const rs = data?.retro_scan;
      if (rs?.error) {
        alert(`키워드를 등록했습니다.\n\n다만 최근 메일 소급 검색은 실패했습니다:\n${rs.error}`);
      } else if (rs?.new_matches > 0) {
        alert(`키워드를 등록했습니다.\n최근 30일 메일에서 ${rs.new_matches}건을 찾아 알림에 추가했습니다.`);
      }
    } catch (err) {
      console.error("❌ 키워드 등록 실패:", err);
      alert("키워드 등록에 실패했습니다. (중복된 키워드일 수 있습니다)");
    }
  }

  async function toggleKeyword(kw) {
    try {
      await api.patch(`/mail-alert/keywords/${kw.id}/`, { is_active: !kw.is_active });
      loadKeywords();
    } catch (err) {
      console.error("❌ 키워드 상태 변경 실패:", err);
    }
  }

  async function deleteKeyword(kw) {
    if (!window.confirm(`"${kw.keyword}" 키워드를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/mail-alert/keywords/${kw.id}/`);
      loadKeywords();
    } catch (err) {
      console.error("❌ 키워드 삭제 실패:", err);
    }
  }

  async function addRecipient(staff) {
    try {
      await api.post("/mail-alert/recipients/", { user_id: staff.id });
      setStaffInput("");
      setShowStaffList(false);
      loadRecipients();
    } catch (err) {
      console.error("❌ 수신자 등록 실패:", err);
      alert("수신자 등록에 실패했습니다. (이미 등록된 직원일 수 있습니다)");
    }
  }

  async function deleteRecipient(r) {
    if (!window.confirm(`"${r.user_name}"님을 수신자에서 제외하시겠습니까?`)) return;
    try {
      await api.delete(`/mail-alert/recipients/${r.id}/`);
      loadRecipients();
    } catch (err) {
      console.error("❌ 수신자 삭제 실패:", err);
    }
  }

  async function saveStatusConfig() {
    setSavingStatus(true);
    try {
      const { data } = await api.patch("/mail-alert/status/", {
        resend_interval_minutes: Number(status.resend_interval_minutes) || 60,
        max_resend_count: Number(status.max_resend_count) || 24,
      });
      setStatus(data);
      alert("저장되었습니다.");
    } catch (err) {
      console.error("❌ 설정 저장 실패:", err);
      alert("설정 저장에 실패했습니다.");
    } finally {
      setSavingStatus(false);
    }
  }

  async function checkNow() {
    setCheckingNow(true);
    try {
      const { data } = await api.post("/mail-alert/check-now/");
      if (data.error) {
        alert(`조회 실패: ${data.error}`);
      } else {
        alert(`조회 완료 — 확인 ${data.checked}건 · 신규 매칭 ${data.new_matches}건 · 재알림 ${data.resent}건 · 만료 ${data.expired}건`);
      }
      loadStatus();
      setRunsRefreshKey((k) => k + 1);   // ✅ 이력 패널에 방금 회차가 바로 뜨게
    } catch (err) {
      console.error("❌ 지금 확인 실패:", err);
      alert("지금 확인에 실패했습니다.");
    } finally {
      setCheckingNow(false);
    }
  }

  // ✅ 최근 N일 재검색 — 활성 키워드 전체로 전 폴더를 되짚는다. 기본 "지금 확인"은
  // 신착만 보므로(커서 이후), 놓친 과거 메일을 잡으려면 이 모드가 필요하다.
  // 알림이 실제로 나가므로 확인창을 반드시 거친다.
  async function rescanRecent() {
    if (!window.confirm(
      "활성 키워드 전체로 최근 30일 메일을 다시 검색합니다.\n" +
      "(받은편지함뿐 아니라 '청구·결제' 같은 분류 폴더까지 훑습니다)\n\n" +
      "새로 매칭되는 메일이 있으면 알림이 발송됩니다. 진행할까요?"
    )) return;
    setRescanning(true);
    try {
      // ✅ 서버가 백그라운드로 돌리고 즉시 응답한다 — 예전엔 요청 안에서 끝까지
      // 돌다 타임아웃에 끊겨 뒤쪽 키워드가 실행조차 안 됐다. 진행은 아래 폴링으로.
      const { data } = await api.post("/mail-alert/check-now/", { retro: true, days: 30 });
      if (data.started === false) {
        alert(data.detail || "이미 재검색이 진행 중입니다.");
        setRescanning(false);
        return;
      }
      setRetroState({ running: true, phase: "시작", scanned: 0, new_matches: 0 });
    } catch (err) {
      console.error("❌ 재검색 실패:", err);
      alert("재검색 시작에 실패했습니다.");
      setRescanning(false);
    }
  }

  // ✅ 재검색 진행 폴링 — 8분간 에러인지 진행인지 알 수 없던 문제 해소.
  // rescanning일 때만 2초 간격으로 돌고, 완료되면 요약을 띄우고 멈춘다.
  useEffect(() => {
    if (!rescanning) return undefined;
    let stop = false;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/mail-alert/retro-scan/status/");
        if (stop) return;
        setRetroState(data);
        if (!data.running) {
          clearInterval(timer);
          setRescanning(false);
          const errs = data.errors?.length ? `\n\n건너뜀: ${data.errors.join("\n")}` : "";
          alert(
            `재검색 완료 — 키워드 ${data.keywords}개 · 최근 ${data.days}일\n` +
            `스캔 ${data.scanned}통 · 새 알림 ${data.new_matches}건 ` +
            `(이미 처리됨 ${data.skipped}건)${errs}`
          );
          loadStatus();
        }
      } catch (err) {
        console.error("❌ 진행 조회 실패:", err);
      }
    }, 2000);
    return () => { stop = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescanning]);

  // ── 제외 조건(오탐 필터) ──
  async function saveExclude(kw, patch) {
    try {
      await api.patch(`/mail-alert/keywords/${kw.id}/`, patch);
      loadKeywords();
    } catch (err) {
      console.error("❌ 제외 조건 저장 실패:", err);
      alert("제외 조건 저장에 실패했습니다.");
    }
  }

  function addExclude(kw, field, value) {
    const v = (value || "").trim();
    if (!v) return;
    const list = kw[field] || [];
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    saveExclude(kw, { [field]: [...list, v] });
  }

  function removeExclude(kw, field, value) {
    saveExclude(kw, { [field]: (kw[field] || []).filter((x) => x !== value) });
  }

  return (
    // ⚠️ 이력 패널은 3열 그리드 **밖**, 아래 전체 폭에 둔다. 그리드 열 폭은
    // minmax(300px,1fr)라 시각+요약 한 줄이 그 안에서는 꺾인다(nowrap 규칙 위반).
    <>
    <div style={GRID_STYLE}>
      <div style={SECTION_STYLE}>
        <h3 style={SECTION_TITLE_STYLE}>키워드</h3>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            placeholder="키워드 입력 (예: 수도, 가스, 관리비)"
            style={{ ...INPUT_STYLE, flex: 1 }}
          />
          <button onClick={addKeyword} style={PRIMARY_BTN_STYLE}>+ 등록</button>
        </div>
        {keywords.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>등록된 키워드가 없습니다.</div>}
        {keywords.map((kw) => (
          <div key={kw.id} style={{ borderBottom: "1px solid #eee" }}>
            <div style={{ ...ROW_STYLE, borderBottom: "none" }}>
              <span style={NAME_STYLE} title={kw.keyword}>{kw.keyword}</span>
              <label style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={kw.is_active} onChange={() => toggleKeyword(kw)} /> 활성
              </label>
              {/* ✅ 제외 조건 — 기본은 접힘. 걸린 개수만 뱃지로 보여 목록은 그대로 깔끔하게. */}
              <button
                onClick={() => {
                  setOpenExclude(openExclude === kw.id ? null : kw.id);
                  setExcludeDraft({ only: "", sender: "", word: "" });
                }}
                style={LINK_BTN_STYLE}
                title="이 키워드의 허용 발신자 / 제외 발신자·단어"
              >
                필터{excludeCount(kw) > 0 ? ` ${excludeCount(kw)}` : ""} {openExclude === kw.id ? "▲" : "▼"}
              </button>
              <button onClick={() => deleteKeyword(kw)} style={SMALL_BTN_STYLE} title="삭제">✕</button>
            </div>

            {openExclude === kw.id && (
              <div style={{ padding: "4px 0 12px", display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { field: "include_senders", draft: "only", label: "허용 발신자 (지정하면 이 발신자만)", ph: "예: 쿠팡 (발신자에 이 문자열이 있어야 알림)" },
                  { field: "exclude_senders", draft: "sender", label: "제외 발신자", ph: "예: safety@example.com (일부만 써도 됨)" },
                  { field: "exclude_words", draft: "word", label: "제외 단어", ph: "예: 안전점검" },
                ].map(({ field, draft, label, ph }) => (
                  <div key={field}>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, whiteSpace: "nowrap" }}>{label}</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input
                        value={excludeDraft[draft]}
                        onChange={(e) => setExcludeDraft((p) => ({ ...p, [draft]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          addExclude(kw, field, excludeDraft[draft]);
                          setExcludeDraft((p) => ({ ...p, [draft]: "" }));
                        }}
                        placeholder={ph}
                        style={{ ...INPUT_STYLE, flex: 1, minWidth: 0, height: 30, fontSize: 12 }}
                      />
                      <button
                        onClick={() => {
                          addExclude(kw, field, excludeDraft[draft]);
                          setExcludeDraft((p) => ({ ...p, [draft]: "" }));
                        }}
                        style={{ ...PRIMARY_BTN_STYLE, width: "auto", height: 30, padding: "0 10px", fontSize: 12 }}
                      >
                        추가
                      </button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {(kw[field] || []).length === 0 && (
                        <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>없음</span>
                      )}
                      {(kw[field] || []).map((v) => (
                        <span key={v} style={TAG_STYLE} title={v}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{v}</span>
                          <button onClick={() => removeExclude(kw, field, v)} style={TAG_X_STYLE} title="제거">✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                  허용 발신자를 지정하면 그 발신자에서 온 것만 알립니다. 발신자는 부분일치이고, 제목에 제외 단어가 있으면 무시합니다. 셋 다 비우면 기존과 동일하게 동작합니다.
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ ...SECTION_STYLE, position: "relative" }}>
        <h3 style={SECTION_TITLE_STYLE}>수신자</h3>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input
            value={staffInput}
            onChange={(e) => { setStaffInput(e.target.value); setShowStaffList(true); }}
            onFocus={() => staffInput.trim() && setShowStaffList(true)}
            placeholder="직원 이름으로 검색해 추가"
            style={{ ...INPUT_STYLE, width: "100%" }}
          />
          {showStaffList && staffResults.length > 0 && (
            <ul style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
              border: "1px solid #ced4da", borderRadius: 6, maxHeight: 220, overflowY: "auto",
              zIndex: 10, margin: "2px 0 0", padding: 0, listStyle: "none", boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}>
              {staffResults.map((s) => (
                <li
                  key={s.id}
                  onMouseDown={() => addRecipient(s)}
                  style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}
                >
                  {s.name} <span style={{ color: "#999" }}>{s.position}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {recipients.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>등록된 수신자가 없습니다.</div>}
        {recipients.map((r) => (
          <div key={r.id} style={ROW_STYLE}>
            <span style={NAME_STYLE} title={`${r.user_name} ${r.position || ""}`}>
              {r.user_name} <span style={{ color: "#999", fontSize: 12 }}>{r.position}</span>
            </span>
            <button onClick={() => deleteRecipient(r)} style={SMALL_BTN_STYLE} title="삭제">✕</button>
          </div>
        ))}
      </div>

      <div style={SECTION_STYLE}>
        <h3 style={SECTION_TITLE_STYLE}>조회 상태 · 재알림 설정</h3>
        {status && (
          <>
            <div style={{ fontSize: 13, color: "#495057", marginBottom: 4, whiteSpace: "nowrap" }}>
              마지막 조회 성공: {status.last_success_at ? new Date(status.last_success_at).toLocaleString("ko-KR") : "-"}
            </div>
            {/* ✅ 경과 시간 — 10분 주기라 20분을 넘으면 조회가 멈춘 것으로 보고 빨갛게.
                시각만 보여주면 그 값이 지금 것인지 몇 시간 전 것인지 구분이 안 된다. */}
            {staleMinutes !== null && (
              <div style={{
                fontSize: 12, marginBottom: 8, whiteSpace: "nowrap", fontWeight: 600,
                color: staleMinutes >= STALE_MINUTES ? "#be123c" : "#2E7D32",
              }}>
                {staleMinutes <= 0 ? "방금 전" : `${staleMinutes}분 전`}
                {staleMinutes >= STALE_MINUTES && " — 10분 주기가 멈춘 것으로 보입니다"}
              </div>
            )}
            {status.last_error && (
              <div style={{ fontSize: 13, color: "#be123c", marginBottom: 12, whiteSpace: "nowrap" }}>
                최근 오류: {status.last_error}
              </div>
            )}
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                재알림 간격(분)
                <input
                  type="number" min={1}
                  value={status.resend_interval_minutes}
                  onChange={(e) => setStatus((prev) => ({ ...prev, resend_interval_minutes: e.target.value }))}
                  style={{ ...INPUT_STYLE, width: 80 }}
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                최대 재알림 횟수
                <input
                  type="number" min={1}
                  value={status.max_resend_count}
                  onChange={(e) => setStatus((prev) => ({ ...prev, max_resend_count: e.target.value }))}
                  style={{ ...INPUT_STYLE, width: 80 }}
                />
              </label>
              <button onClick={saveStatusConfig} disabled={savingStatus} style={PRIMARY_BTN_STYLE}>
                {savingStatus ? "저장 중…" : "설정 저장"}
              </button>
              <button onClick={checkNow} disabled={checkingNow} style={{ ...PRIMARY_BTN_STYLE, background: "#2E7D32" }}>
                {checkingNow ? "확인 중…" : "지금 확인"}
              </button>
              {/* ✅ 소급 재검색 — "지금 확인"은 신착만 보므로 놓친 과거 메일은 못 잡는다.
                  width를 명시해 전역 button{width:100%}에 안 잡히게 한다. */}
              <button
                onClick={rescanRecent}
                disabled={rescanning}
                title="활성 키워드 전체로 최근 30일 메일을 전 폴더에서 다시 검색합니다"
                style={{ ...PRIMARY_BTN_STYLE, width: "auto", background: "#6d28d9" }}
              >
                {rescanning ? "재검색 중…" : "최근 30일 재검색"}
              </button>
            </div>
            {/* ✅ 진행 표시 — 백그라운드로 도는 동안 어디까지 갔는지 보여준다.
                예전엔 몇 분간 아무 표시가 없어 진행 중인지 실패인지 구분이 안 됐다. */}
            {rescanning && retroState && (
              <div style={{
                marginTop: 10, padding: "8px 10px", borderRadius: 6,
                background: "#f5f3ff", color: "#5b21b6", fontSize: 12, lineHeight: 1.6,
              }}>
                <div style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                  재검색 진행 중 — {retroState.phase || "준비"}
                  {retroState.folder ? ` · ${retroState.folder}` : ""}
                </div>
                <div style={{ whiteSpace: "nowrap" }}>
                  스캔 {retroState.scanned ?? 0}통 · 새 알림 {retroState.new_matches ?? 0}건
                  {retroState.skipped ? ` · 이미 처리됨 ${retroState.skipped}건` : ""}
                </div>
                <div style={{ color: "#7c6ba8" }}>
                  창을 닫아도 서버에서 계속 진행되며, 완료되면 알림함에 반영됩니다.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    <MailCheckRunsPanel refreshKey={runsRefreshKey} onOpenInbox={onOpenInbox} />
    </>
  );
}
